import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Grid, Tab, Tabs, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useShallow } from "zustand/react/shallow";

import globalStyles from "../../../../../../assets/styles/global.module.scss";
import styles from "../../../../../../assets/styles/componentStyles/organizationTeamsStyles/teams.module.scss";

import { UserContext, UserContextPayload } from "../../../../../../context/userContext";
import { useOrganizationTeamsStore } from "../../../../../../store/organizationTeamsStore";

import TeamOverview from "./teamOverview";
import TeamMembers from "./teamMembers";
import TeamSettings from "./teamSettings";
import { getOrganizationUserDataForTeams } from "../../utils/getOrganizationUserData";
import agent from "../../../../../../services/api";
import { hasOrganizationTeamManageAccess } from "../../../../../../utils/constants";
import { formatOrganizationTeam } from "../../constant.teams";

const TeamDetails = () => {

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const team_id = searchParams.get("team_id");
    const organization_id = searchParams.get("organization_id");

    const [selectedTab, setSelectedTab] = useState(0);

    const { organizationDetails, userDetail, userAssignmentRoleData, apiRoutesPermissionAssignment, setFlagLoading, handleError } = useContext(UserContext) as UserContextPayload;

    const { selectedTeam, setSelectedTeam } = useOrganizationTeamsStore(
        useShallow((state) => ({
            selectedTeam: state.selectedTeam,
            setSelectedTeam: state.setSelectedTeam,
        }))
    );

    const organizationUserData = useMemo(
        () => getOrganizationUserDataForTeams({ organizationDetails, organization_id, timezoneHour: userDetail?.timezone_hour, userAssignmentRoleData }),
        [organizationDetails, organization_id, userDetail?.timezone_hour, userAssignmentRoleData]
    );

    // Back to Teams
    const handleBackToTeams = () => {
        setSelectedTeam(null);
        navigate(`/organization/teams?organization_id=${organization_id}`);
    };

    // Team Details
    const getTeamDetails = async () => {
        if (!team_id) {
            return;
        }

        setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: true }));
        
        const res = await agent.getOrganizationTeamDetails.get(team_id ?? "");
        
        if (res.status) {
            setSelectedTeam(formatOrganizationTeam(res?.data));
        } else {
            handleError(res);
        }
        
        setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: false }));
    }

    useEffect(() => {
        if (team_id) {
            getTeamDetails();
        }
        else {
            handleBackToTeams();
        }
    }, [team_id]);

    if (!selectedTeam) {
        return null;
    }

    return (
        <Grid item xs={12}>
            <Grid container direction="column" className={styles.teamDetailsContainer}>
                
                <Box className={styles.teamDetailsBackLink} onClick={handleBackToTeams} role="button" tabIndex={0}>
                    <ArrowBackIcon fontSize="small" className={styles.teamDetailsBackIcon} />
                    <Typography component="span" noWrap className={styles.teamDetailsBackText}>
                        Back to Teams
                    </Typography>
                </Box>

                <Grid container className={styles.teamDetailsContent}>
                    <Grid item className={globalStyles.fullwidth}>
                        <TeamOverview team={selectedTeam} />
                    </Grid>

                    {hasOrganizationTeamManageAccess(apiRoutesPermissionAssignment, userDetail) ?
                        <Grid item className={`${globalStyles.fullwidth} ${styles.teamDetailsTabContainer}`}>
                            <Tabs
                                value={selectedTab}
                                onChange={(_, value) => setSelectedTab(value)}
                                aria-label="team details tabs"
                                className={styles.teamDetailsTabs}
                            >
                                <Tab label="Members" className={styles.teamDetailsTabsItem} />
                                <Tab label="Settings" className={styles.teamDetailsTabsItem} />
                            </Tabs>

                            <Box className={`${globalStyles.fullwidth}`}>
                                {selectedTab === 0 && (
                                    <TeamMembers team={selectedTeam} organizationUserData={organizationUserData} />
                                )}

                                {selectedTab === 1 && <TeamSettings team={selectedTeam} />}
                            </Box>
                        </Grid>
                    : 
                        <Grid item className={globalStyles.fullwidth}>
                            <TeamMembers team={selectedTeam} organizationUserData={organizationUserData} />
                        </Grid>
                    }
                </Grid>

            </Grid>
        </Grid>
    );
};

export default TeamDetails;